import _ from 'lodash';
import Markdown from 'markdown-to-jsx';
import React, { useContext, useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { useParams } from 'react-router-dom';
import { MessagingContext } from '../../../services';
import { BaseInput } from '../../base';
import { AttachmentButton } from '../attachment-button';
import { MessagingPill } from '../messaging-badge';
import { MessageFieldsRedesigned, IMessageFieldsValues } from '../message-fields';
import { ClaimsManager } from '../claims-manager';
import { isHtmlContent, sanitizeHtml, normalizeMarkdown } from '../util';
import * as S from './chat-body.styles';
import { BlockedComponent } from './blocked-component';
import { CoreFetch, MessageLinkEntityType } from '../../../api';
import { MessageApi } from '../../../api/server';
import { CodingsTable } from './codings-table';
import { authorNickname } from '../util';

interface IChatBody {
    hideNote?: boolean;
    hideDone?: boolean;
}

export const ChatBody: React.FunctionComponent<IChatBody> = (props: IChatBody): React.ReactElement => {
    const { hideNote, hideDone } = props;

    // Get context and data BEFORE any hooks
    const context = useContext(MessagingContext);
    const { accountId } = useParams<{ accountId?: string }>();
    const comment = _.first(context.comments);
    const isReadOnlyClaimChoice = context.isReadOnlyClaimChoice;
    const isSupportApp = context.account?.supportGrant === true;
    const nickname = authorNickname(context.message as any);       

    // Parse ISO date string to just date portion (yyyy-MM-dd)
    const parseDateOnlyInitial = (isoString: string | undefined | null): string | undefined => {
        if (!isoString) return undefined;
        return isoString.split('T')[0];
    };

    // All useState hooks MUST be called before any conditional returns
    const [isEditable, setEditable] = useState(false);
    const [isSubjectLoading, setSubjectLoading] = useState(false);
    const [subjectView, setSubjectView] = useState(context.message?.subject || '');
    const [locked, setLocked] = useState(false);
    const [messageFields, setMessageFields] = useState<IMessageFieldsValues>({
        priority: context.message?.priority || undefined,
        dueDate: parseDateOnlyInitial(context.message?.dueDate),
        categoryIds: _.map(context.message?.links?.messageCategories, 'id') || [],
        assignedUserIds: _.map(context.message?.links?.users, 'id') || [],
    });

    // Field-specific loading states
    const [isCategorySaving, setIsCategorySaving] = useState(false);
    const [isAssignedUsersSaving, setIsAssignedUsersSaving] = useState(false);

    // Ref to store debounced save function
    const debouncedSaveRef = useRef<ReturnType<typeof _.debounce> | null>(null);

    // Helper function - must be defined before hooks that use it
    const parseDateOnly = (isoString: string | undefined | null): string | undefined => {
        if (!isoString) return undefined;
        return isoString.split('T')[0];
    };

    // All useMemo hooks MUST be called before any conditional returns
    const claimIds = useMemo(() => {
        return _.map(context.message?.links?.claims, 'id') || [];
    }, [context.message?.links?.claims]);

    const codingIds = useMemo(() => {
        return _.map(context.message?.links?.codings, 'id') || [];
    }, [context.message?.links?.codings]);

    // Check if there are actual changes - useCallback must be before early return
    const hasChanges = useCallback((): boolean => {
        if (!context.message) return false;
        const original = {
            priority: context.message.priority || undefined,
            dueDate: parseDateOnly(context.message.dueDate),
            categoryIds: _.map(context.message.links?.messageCategories, 'id') || [],
            assignedUserIds: _.map(context.message.links?.users, 'id') || [],
        };
        return !_.isEqual(messageFields, original);
    }, [context.message, messageFields]);

    // Reusable save function - useCallback must be before early return
    const saveMessageFields = useCallback(async () => {
        if (!context.message?.id) {
            return;
        }
        const id = context.message.id;

        const original = {
            priority: context.message.priority || undefined,
            dueDate: parseDateOnly(context.message.dueDate),
            categoryIds: _.map(context.message.links?.messageCategories, 'id') || [],
            assignedUserIds: _.map(context.message.links?.users, 'id') || [],
        };

        if (_.isEqual(messageFields, original)) {
            return;
        }
        let hasError = false;

        setIsAssignedUsersSaving(true);
        try {
            const existingUserIds = _.map(context.message.links?.users, 'id') || [];
            const existingUserIdsSet = new Set(existingUserIds);
            const selectedUserIds = messageFields.assignedUserIds || [];

            const newUserIds = selectedUserIds.filter(userId => !existingUserIdsSet.has(userId));
            if (newUserIds.length > 0) {
                await context.onLinkEntityToMessage?.(id, MessageLinkEntityType.BANKUSER, newUserIds);
            }

            const selectedUserIdsSet = new Set(selectedUserIds);
            const removedUserIds = existingUserIds.filter(userId => !selectedUserIdsSet.has(userId));
            if (removedUserIds.length > 0) {
                await context.onUnlinkEntityFromMessage?.(id, MessageLinkEntityType.BANKUSER, removedUserIds);
            }
            setIsAssignedUsersSaving(false);
        } catch (error) {
            hasError = true;
            setIsAssignedUsersSaving(false);
        }

        setIsCategorySaving(true);
        try {
            const existingCategoryIds = _.map(context.message.links?.messageCategories, 'id') || [];
            const existingCategoryIdsSet = new Set(existingCategoryIds);
            const selectedCategoryIds = messageFields.categoryIds || [];

            const newCategoryIds = selectedCategoryIds.filter(catId => !existingCategoryIdsSet.has(catId));
            if (newCategoryIds.length > 0) {
                await context.onLinkEntityToMessage?.(id, MessageLinkEntityType.MESSAGECATEGORY, newCategoryIds);
            }

            const selectedCategoryIdsSet = new Set(selectedCategoryIds);
            const removedCategoryIds = existingCategoryIds.filter(catId => !selectedCategoryIdsSet.has(catId));
            if (removedCategoryIds.length > 0) {
                await context.onUnlinkEntityFromMessage?.(
                    id,
                    MessageLinkEntityType.MESSAGECATEGORY,
                    removedCategoryIds,
                );
            }
            setIsCategorySaving(false);
        } catch (error) {
            console.error('Failed to update categories:', error);
            hasError = true;
            setIsCategorySaving(false);
        }

        try {
            const previousPriority = context.message.priority;
            const newPriority = messageFields.priority;
            const didPriorityChange = newPriority !== previousPriority;

            const previousDueDateString = parseDateOnly(context.message.dueDate);
            const newDueDateString = messageFields.dueDate;
            const didDueDateChange = newDueDateString !== previousDueDateString;

            if (didPriorityChange || didDueDateChange) {
                let dueDateISO: string | null = null;
                if (newDueDateString) {
                    const [year, month, day] = newDueDateString.split('-').map(Number);
                    dueDateISO = new Date(Date.UTC(year, month - 1, day, 0, 0, 0, 0)).toISOString();
                }

                // Use context callback if available (preferred - goes through store for proper list sync)
                if (context.onUpdateMessage) {
                    await context.onUpdateMessage(id, {
                        priority: newPriority,
                        dueDate: dueDateISO,
                    });
                } else {
                    // Fallback to direct API call
                    if (isSupportApp) {
                        await CoreFetch(MessageApi).putSupportMessage({
                            id,
                            supportUpdateMessagePayload: {
                                priority: newPriority as any,
                                dueDate: dueDateISO || undefined,
                            },
                        });
                    } else {
                        await CoreFetch(MessageApi).putProviderMessage({
                            id,
                            providerUpdateMessagePayload: {
                                priority: newPriority as any,
                                dueDate: dueDateISO || undefined,
                            },
                        });
                    }
                }
            }
        } catch (error) {
            console.error('Failed to update priority/due date:', error);
            hasError = true;
        }

        if (hasError) {
            throw new Error('Failed to update priority/due date');
        }

        // Note: Individual callbacks (onLinkEntityToMessage, onUnlinkEntityFromMessage, onUpdateMessage)
        // already handle refreshing the message detail and syncing to the list.
        // Only call onMessageFieldsUpdate for fallback case when onUpdateMessage wasn't available.
        if (!context.onUpdateMessage) {
            context.onMessageFieldsUpdate?.();
        }
    }, [
        context.message,
        messageFields,
        context.onLinkEntityToMessage,
        context.onUnlinkEntityFromMessage,
        context.onUpdateMessage,
        context.onMessageFieldsUpdate,
        isSupportApp,
    ]);

    // All useEffect hooks must be before early return
    useEffect(() => {
        if (context.message?.subject) {
            setSubjectView(context.message.subject);
        }
    }, [context.message?.subject]);

    useEffect(() => {
        if (!context.message) return;
        setMessageFields({
            priority: context.message.priority || undefined,
            dueDate: parseDateOnly(context.message.dueDate),
            categoryIds: _.map(context.message.links?.messageCategories, 'id') || [],
            assignedUserIds: _.map(context.message.links?.users, 'id') || [],
        });
    }, [
        context.message?.id,
        context.message?.priority,
        context.message?.dueDate,
        context.message?.links?.messageCategories,
        context.message?.links?.users,
    ]);

    useEffect(() => {
        if (debouncedSaveRef.current) {
            debouncedSaveRef.current.cancel();
        }
        debouncedSaveRef.current = _.debounce(saveMessageFields, 800);
        return () => {
            debouncedSaveRef.current?.cancel();
        };
    }, [saveMessageFields]);

    useEffect(() => {
        if (!context.message) return;
        if (hasChanges() && debouncedSaveRef.current) {
            debouncedSaveRef.current();
        }
    }, [messageFields, hasChanges, context.message]);

    // NOW we can do conditional returns AFTER all hooks are declared
    if (!context.message || !comment) {
        return <></>;
    }

    const hasNote = !hideNote && _.get(comment, 'note', false);

    const { created, updated, id, links } = context.message;

    const commentDocIds = _.get(comment, 'documents', []) as string[];
    const documents = _.filter(context.documents, document => {
        return _.includes(commentDocIds, document.id);
    });

    // Check if documents are still loading (comment has doc IDs but documents not loaded yet)
    const isDocumentsLoading = commentDocIds.length > 0 && documents.length === 0;

    const renderAttachments = (): React.ReactElement | React.ReactElement[] | undefined => {
        // Show loading indicator when documents are being fetched
        if (isDocumentsLoading) {
            return <S.LoadingText>Loading documents...</S.LoadingText>;
        }
        if (!documents || !documents.length) {
            return;
        }
        return _.times(_.size(documents), (i: number): React.ReactElement => {
            const document = documents[i];
            const onDownload = async (): Promise<void> => {
                if (document && document.id) {
                    await (context.onDocumentDownload && context.onDocumentDownload(document.id));
                }
            };

            const onAssign = async (): Promise<void> => {
                await (context.onDocumentAssign && context.onDocumentAssign(document));
            };

            const onEdit = async (): Promise<void> => {
                await (context.onDocumentEdit && context.onDocumentEdit(document));
            };

            const canAssign = claimIds.length > 0 && !document.claimId && !!context.onDocumentAssign;

            return (
                <AttachmentButton
                    key={`attachment-${i}`}
                    filename={document.filename as string}
                    filesize={document.filesize}
                    thumbnail={document.thumbnail}
                    onDownload={onDownload}
                    onAssign={canAssign ? onAssign : undefined}
                    onEdit={context.onDocumentEdit ? onEdit : undefined}
                    isSupportApp={isSupportApp}
                />
            );
        });
    };

    const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        setSubjectView(e.target.value);
    };

    const handleBlur = async (e: React.FocusEvent<HTMLInputElement>) => {
        setSubjectView(e.target.value);
        if (!id) {
            throw new Error("Can't edit subject for this message");
        }
        setSubjectLoading(true);
        await context.onSubjectUpdate(id, e.target.value);
        setSubjectLoading(false);
        handleToggleEditable();
    };

    const handleToggleEditable = () => {
        if (!context.isSubjectEditable) {
            return;
        }

        setEditable(!isEditable);
    };

    const handleDoneClick = async (): Promise<void> => {
        if (!locked && context.message && context.message.id) {
            setLocked(true);
            await context.onMarkAsClosed(context.message.id);
            setLocked(false);
            context.onMessageClose();
        }
    };

    const handleUndoneClick = async (): Promise<void> => {
        if (!locked && context.message && context.message.id) {
            setLocked(true);
            await context.onMarkAsOpen(context.message.id);
            setLocked(false);
            context.onMessageClose();
        }
    };

    const handleReadClick = async (): Promise<void> => {
        if (locked || !context.message || !context.message.id) {
            return;
        }
        setLocked(true);
        try {
            await context.onMarkAsRead(context.message.id);
        } finally {
            setLocked(false);
        }
    };

    const handleUnreadClick = async (): Promise<void> => {
        if (locked || !context.message || !context.message.id) {
            return;
        }
        setLocked(true);
        try {
            await context.onMarkAsUnread(context.message.id);
        } finally {
            setLocked(false);
        }
    };

    const handleRemoveClaim = (claimId: string) => async () => {
        if (!id) {
            throw new Error("Can't edit claim for this message");
        }

        try {
            await context.onUnlinkEntityFromMessage?.(id, MessageLinkEntityType.CLAIM, [claimId]);
        } catch (error) {
            console.error('Failed to unlink claim:', error);
        }
    };

    const handleChangeClaim = async (claimId: string) => {
        if (!id) {
            throw new Error("Can't edit claim for this message");
        }
        await context.onLinkEntityToMessage?.(id, MessageLinkEntityType.CLAIM, [claimId]);
    };

    const handleBulkClaimsAdd = async (claimIds: string[]) => {
        if (!id) {
            throw new Error("Can't edit claim for this message");
        }
        await context.onLinkEntityToMessage?.(id, MessageLinkEntityType.CLAIM, claimIds);
    };

    const renderClaimButtons = (): React.ReactElement[] => {
        return _.map(claimIds, (claimId: string) => (
            <S.ClaimButton
                key={claimId}
                claimId={claimId}
                onRemove={handleRemoveClaim(claimId)}
                forceClaim={!!isReadOnlyClaimChoice}
            />
        ));
    };

    const handleMessageFieldsChange = (newValues: IMessageFieldsValues) => {
        setMessageFields(newValues);
    };

    return (
        <S.Header note={hasNote} aiNote={nickname === 'Enter Support'}>
            <S.MessageGrid>
                <S.Row>{nickname === 'Enter Support' && <MessagingPill>AI Message</MessagingPill>}</S.Row>
                <S.Row>{!hideNote && hasNote && <MessagingPill note={true}>Internal Note</MessagingPill>}</S.Row>
                {isEditable ? (
                    <S.EditableSubject title="Edit">
                        <BaseInput
                            onChange={handleChange}
                            disabled={isSubjectLoading}
                            onBlur={handleBlur}
                            value={subjectView}
                            autoFocus={true}
                        />
                    </S.EditableSubject>
                ) : (
                    <S.Subject onClick={handleToggleEditable}>{subjectView}</S.Subject>
                )}
                <S.Body>
                    {isHtmlContent(comment.body) ? (
                        <div dangerouslySetInnerHTML={{ __html: sanitizeHtml(comment.body) }} />
                    ) : (
                        <Markdown
                            options={{
                                overrides: {
                                    iframe: {
                                        component: BlockedComponent,
                                    },
                                },
                            }}>
                            {normalizeMarkdown(comment.body || '')}
                        </Markdown>
                    )}
                </S.Body>
                <S.Attachments>{renderAttachments()}</S.Attachments>
            </S.MessageGrid>
            <S.MetaSection>
                <MessageFieldsRedesigned
                    values={messageFields}
                    onChange={handleMessageFieldsChange}
                    isCategorySaving={isCategorySaving}
                    isAssignedUsersSaving={isAssignedUsersSaving}
                    read={context.message.read}
                    onReadChange={v => (v ? handleReadClick() : handleUnreadClick())}
                    readDisabled={locked}
                    done={!context.message.open}
                    onDoneChange={v => (v ? handleDoneClick() : handleUndoneClick())}
                    doneDisabled={locked}
                    hideDone={hideDone}
                />
            </S.MetaSection>
            <S.SectionsTable>
                <S.SectionRow>
                    <ClaimsManager
                        messageId={id}
                        claimIds={claimIds}
                        onClaimAdd={handleChangeClaim}
                        onBulkClaimsAdd={handleBulkClaimsAdd}
                        onClaimRemove={(claimId: string) => handleRemoveClaim(claimId)()}
                    />
                </S.SectionRow>
                {codingIds.length > 0 && (
                    <S.SectionRow>
                        <CodingsTable codingIds={codingIds} isSupportApp={isSupportApp} />
                    </S.SectionRow>
                )}
            </S.SectionsTable>
        </S.Header>
    );
};
